import { useState } from "react";
import {
    ArrowRight,
    Dumbbell,
    UtensilsCrossed,
    Syringe,
    Scissors,
    Quote,
    Play,
    X,
} from "lucide-react";
import useReveal from "../hooks/useReveal";

const phases = [
    {
        n: "01",
        title: "Primera Visita",
        description:
            "Evaluación médica integral. Estudiamos historia clínica, composición corporal y perfil metabólico para definir tu punto de partida.",
    },
    {
        n: "02",
        title: "Tratamiento Individualizado",
        description:
            "Plan a la medida: nutrición clínica, agonistas de GLP-1 (Mounjaro, Ozempic, Wegovy o Rybelsus) y — cuando está indicado — cirugía bariátrica.",
    },
    {
        n: "03",
        title: "Mantenimiento",
        description:
            "Seguimiento clínico continuo, ajuste de hábitos y consolidación de resultados sostenibles. El cambio se mantiene con acompañamiento real.",
    },
];

const pillars = [
    {
        icon: Dumbbell,
        pct: "50%",
        title: "Ejercicio",
        body: "Pilar base. Ejercicio regular para mejorar el metabolismo, proteger la masa muscular y crear hábitos sostenibles.",
    },
    {
        icon: UtensilsCrossed,
        pct: "30%",
        title: "Alimentación",
        body: "Planes nutricionales personalizados, basados en evidencia, adaptados al metabolismo y estilo de vida de cada paciente.",
    },
    {
        icon: Syringe,
        pct: "15%",
        title: "GLP-1",
        body: "Herramienta farmacológica de apoyo cuando ejercicio y alimentación por sí solos no han sido suficientes. Reduce apetito y aumenta saciedad.",
    },
    {
        icon: Scissors,
        pct: "5%",
        title: "Cirugía",
        body: "El tratamiento más efectivo y duradero en pacientes bien seleccionados. Indicada según guías de la ASMBS como parte de un enfoque integral.",
    },
];

const surgeries = [
    {
        title: "Manga Gástrica",
        bullets: [
            "Reduce el tamaño del estómago para comer menos y sentir saciedad más rápido.",
            "Disminuye el apetito y favorece una pérdida de peso importante.",
            "Mejora diabetes, hipertensión y apnea del sueño.",
            "Resultados duraderos cuando se acompaña de cambios en estilo de vida.",
        ],
    },
    {
        title: "Bypass Gástrico",
        bullets: [
            "Crea un estómago más pequeño y modifica el recorrido de los alimentos.",
            "Pérdida de peso profunda y sostenida.",
            "Fuerte efecto en el control de la diabetes tipo 2.",
            "Mejora múltiples enfermedades asociadas a la obesidad.",
        ],
    },
];

const ProtocoloPuntoCero = () => {
    const ref = useReveal();
    const ref2 = useReveal();
    const ref3 = useReveal();
    const ref4 = useReveal();
    const ref5 = useReveal();
    const ref6 = useReveal();
    const refVideo = useReveal();
    const [videoOpen, setVideoOpen] = useState(false);

    const scrollTo = (id) => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
    };

    return (
        <section
            id="punto-cero"
            data-testid="punto-cero-section"
            className="relative bg-navy text-cream overflow-hidden"
        >
            <div className="absolute inset-0 paper-grain opacity-30 pointer-events-none" />
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/50 to-transparent" />
            <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-gold/[0.04] blur-3xl pointer-events-none" />

            <div className="relative max-w-6xl mx-auto px-6 lg:px-8 py-28 lg:py-36">
                {/* Header */}
                <div ref={ref} className="reveal text-center">
                    <div className="flex items-center justify-center gap-5 mb-10">
                        <span className="block w-24 h-px bg-gold" />
                        <span className="eyebrow">Protocolo exclusivo</span>
                        <span className="block w-24 h-px bg-gold" />
                    </div>

                    <h2
                        data-testid="punto-cero-heading"
                        className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl tracking-tight text-cream"
                    >
                        Protocolo
                        <br />
                        <span className="italic text-gold">Punto Cero</span>
                    </h2>

                    <p className="mt-8 font-display italic text-2xl sm:text-3xl text-cream/85">
                        Donde la ciencia mejora tu vida.
                    </p>
                </div>

                {/* Misión / Visión */}
                <div
                    ref={ref2}
                    className="reveal mt-16 grid md:grid-cols-2 gap-6 lg:gap-8 max-w-4xl mx-auto"
                >
                    <div className="border-l-2 border-gold/70 pl-6 py-2">
                        <span className="eyebrow">Misión</span>
                        <p className="mt-3 text-cream/80 leading-relaxed text-[14px] sm:text-[15px] font-light">
                            Ser un espacio donde las personas recuperen su salud, confianza y
                            bienestar, transformando su vida a través de un acompañamiento médico
                            integral y humano.
                        </p>
                    </div>
                    <div className="border-l-2 border-gold/70 pl-6 py-2">
                        <span className="eyebrow">Visión</span>
                        <p className="mt-3 text-cream/80 leading-relaxed text-[14px] sm:text-[15px] font-light">
                            Caminar junto a cada paciente en su proceso de cambio, ofreciendo
                            tratamientos personalizados, seguros y basados en ciencia, con
                            empatía, compromiso y resultados duraderos.
                        </p>
                    </div>
                </div>

                {/* Intro */}
                <div
                    ref={ref3}
                    className="reveal mt-16 max-w-3xl mx-auto text-cream/75 leading-relaxed text-[15px] sm:text-base font-light text-center space-y-6"
                >
                    <p>
                        Somos una clínica médica integral especializada en{" "}
                        <span className="font-medium text-cream">obesidad, bariatría,
                        nutrición clínica y medicina estética</span>, en{" "}
                        <span className="font-medium text-cream">
                            Nutribeauty Clinic & Metabolik Center
                        </span>
                        . Cada tratamiento es personalizado, basado en evidencia y
                        multidisciplinario.
                    </p>
                    <p className="font-display italic text-xl sm:text-2xl text-gold-soft pt-2">
                        Resultados que nacen de la medicina.
                    </p>
                </div>

                {/* Video showcase */}
                <div ref={refVideo} className="reveal mt-16 relative max-w-md mx-auto">
                    <div className="absolute -top-3 -left-3 right-3 bottom-3 border border-gold/30 pointer-events-none" />
                    <button
                        type="button"
                        data-testid="punto-cero-video-trigger"
                        onClick={() => setVideoOpen(true)}
                        className="group relative block w-full bg-navy-deep overflow-hidden text-left"
                        style={{ aspectRatio: "9 / 13" }}
                        aria-label="Reproducir video en quirófano"
                    >
                        <img
                            src="/gallery/quirofano-poster.jpg"
                            alt="Dr. Erik Cárdenas en quirófano"
                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                            loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-navy-deep/95 via-navy-deep/15 to-transparent" />

                        {/* Play button */}
                        <span className="absolute inset-0 flex items-center justify-center">
                            <span className="relative flex items-center justify-center w-20 h-20 border border-gold/70 bg-navy-deep/70 backdrop-blur-sm text-gold group-hover:bg-gold group-hover:text-navy transition-all duration-500">
                                <Play size={28} strokeWidth={1.5} className="ml-1" />
                                <span className="absolute inset-0 border border-gold/40 animate-ping opacity-50" />
                            </span>
                        </span>

                        <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between bg-navy-deep/85 backdrop-blur-sm border border-gold/30 px-3 py-2.5">
                            <span className="font-display text-cream text-sm sm:text-base leading-tight">
                                Dr. Erik Cárdenas · Quirófano
                            </span>
                            <span className="flex items-center gap-2 text-[9px] tracking-[0.28em] uppercase text-gold">
                                <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" />
                                Ver video
                            </span>
                        </div>
                    </button>
                </div>

                {/* Video modal */}
                {videoOpen && (
                    <div
                        data-testid="punto-cero-video-modal"
                        className="fixed inset-0 z-[60] bg-navy-deep/95 backdrop-blur-md flex items-center justify-center p-4 sm:p-8"
                        onClick={() => setVideoOpen(false)}
                    >
                        <button
                            type="button"
                            data-testid="video-modal-close"
                            onClick={() => setVideoOpen(false)}
                            aria-label="Cerrar video"
                            className="absolute top-5 right-5 w-12 h-12 border border-gold/40 text-gold hover:bg-gold hover:text-navy transition-all flex items-center justify-center z-10"
                        >
                            <X size={20} />
                        </button>
                        <video
                            data-testid="punto-cero-video"
                            controls
                            autoPlay
                            playsInline
                            preload="auto"
                            poster="/gallery/quirofano-poster.jpg"
                            onClick={(e) => e.stopPropagation()}
                            className="max-w-full max-h-full w-auto h-auto"
                            style={{ maxHeight: "90vh", aspectRatio: "9 / 16" }}
                            src="/gallery/quirofano.mp4"
                        />
                    </div>
                )}

                {/* Nuestro método (3 fases) */}
                <div className="mt-24">
                    <div className="text-center mb-12">
                        <span className="eyebrow">Nuestro Método</span>
                        <h3 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl text-cream">
                            Tres fases. <span className="italic text-gold-soft">Un solo camino.</span>
                        </h3>
                    </div>
                    <div className="grid md:grid-cols-3 gap-5 lg:gap-7">
                        {phases.map((p, i) => (
                            <article
                                key={p.n}
                                data-testid={`punto-cero-phase-${i}`}
                                className="border border-cream/15 p-7 lg:p-8 bg-navy-deep/40 hover:border-gold/60 hover:bg-navy-deep/70 transition-all duration-500 group"
                            >
                                <div className="flex items-baseline justify-between mb-5">
                                    <span className="font-display text-gold text-4xl leading-none">
                                        {p.n}
                                    </span>
                                    <span className="text-[10px] tracking-[0.3em] uppercase text-cream/40">
                                        Fase
                                    </span>
                                </div>
                                <h4 className="font-display text-2xl lg:text-[1.65rem] text-cream leading-tight">
                                    {p.title}
                                </h4>
                                <div className="my-4 h-px w-10 bg-gold/50 group-hover:w-16 transition-all duration-500" />
                                <p className="text-[14px] leading-relaxed text-cream/70 font-light">
                                    {p.description}
                                </p>
                            </article>
                        ))}
                    </div>
                </div>

                {/* La Meta */}
                <div ref={ref4} className="reveal mt-24">
                    <div className="text-center mb-12">
                        <span className="eyebrow">La Meta</span>
                        <h3 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl text-cream">
                            ¿Qué pesa más en{" "}
                            <span className="italic text-gold-soft">tu resultado?</span>
                        </h3>
                        <p className="mt-4 text-cream/65 font-light text-[14px] sm:text-[15px] max-w-2xl mx-auto">
                            La pérdida de peso sostenida no depende de un solo factor.
                            Distribuimos el esfuerzo con base en evidencia.
                        </p>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
                        {pillars.map((p, i) => (
                            <div
                                key={p.title}
                                data-testid={`meta-pillar-${i}`}
                                className="bg-navy-deep/50 border border-cream/15 p-6 lg:p-7 hover:border-gold/60 transition-colors duration-500 group relative overflow-hidden"
                            >
                                <div className="flex items-center justify-between mb-6">
                                    <span className="w-11 h-11 border border-gold/40 flex items-center justify-center text-gold group-hover:bg-gold group-hover:text-navy transition-all">
                                        <p.icon size={20} strokeWidth={1.5} />
                                    </span>
                                    <span className="font-display text-gold text-4xl lg:text-5xl leading-none">
                                        {p.pct}
                                    </span>
                                </div>
                                <h4 className="font-display text-xl lg:text-2xl text-cream">
                                    {p.title}
                                </h4>
                                <p className="mt-3 text-[13px] text-cream/65 font-light leading-relaxed">
                                    {p.body}
                                </p>
                                <div className="mt-5 h-px w-full bg-cream/10 relative">
                                    <div
                                        className="absolute inset-y-0 left-0 bg-gold transition-all duration-700"
                                        style={{ width: p.pct }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Cirugía bariátrica detail */}
                <div ref={ref5} className="reveal mt-24">
                    <div className="text-center mb-12">
                        <span className="eyebrow">Cirugía Bariátrica</span>
                        <h3 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl text-cream">
                            Dos abordajes,{" "}
                            <span className="italic text-gold-soft">una decisión personalizada.</span>
                        </h3>
                        <p className="mt-4 text-cream/65 font-light text-[14px] sm:text-[15px] max-w-2xl mx-auto">
                            Solo se indica en pacientes bien seleccionados, dentro de un
                            enfoque médico integral.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-5 lg:gap-7">
                        {surgeries.map((s, i) => (
                            <article
                                key={s.title}
                                data-testid={`surgery-card-${i}`}
                                className="border border-cream/15 bg-navy-deep/40 p-7 lg:p-9 hover:border-gold/60 transition-all duration-500 group"
                            >
                                <div className="flex items-center justify-between mb-5">
                                    <h4 className="font-display text-2xl lg:text-3xl text-cream">
                                        {s.title}
                                    </h4>
                                    <span className="font-display text-gold/70 text-2xl">
                                        0{i + 1}
                                    </span>
                                </div>
                                <div className="h-px w-12 bg-gold/60 group-hover:w-20 transition-all duration-500 mb-5" />
                                <ul className="space-y-3">
                                    {s.bullets.map((b) => (
                                        <li
                                            key={b}
                                            className="flex gap-3 text-[14px] text-cream/75 leading-relaxed font-light"
                                        >
                                            <span className="mt-2 w-1 h-1 rounded-full bg-gold shrink-0" />
                                            <span>{b}</span>
                                        </li>
                                    ))}
                                </ul>
                            </article>
                        ))}
                    </div>
                </div>

                {/* Testimonial */}
                <div
                    ref={ref6}
                    className="reveal mt-24 max-w-3xl mx-auto border border-gold/30 bg-navy-deep/30 px-8 sm:px-12 py-12 relative"
                >
                    <Quote
                        size={42}
                        strokeWidth={1.2}
                        className="absolute -top-5 left-8 bg-navy px-2 text-gold"
                    />
                    <p className="font-display italic text-xl sm:text-2xl text-cream leading-relaxed">
                        “Han pasado 6 meses desde que inicié mi tratamiento bariátrico con
                        acompañamiento médico y apoyo con GLP-1. He perdido peso de forma segura,
                        tengo mejor control del apetito y hoy entiendo cómo alimentarme
                        correctamente. La cirugía fue una herramienta; el seguimiento, la
                        nutrición y el GLP-1 han sido clave para mantener los resultados.”
                    </p>
                    <div className="mt-6 flex items-center gap-4">
                        <span className="block w-10 h-px bg-gold" />
                        <span className="text-[11px] tracking-[0.28em] uppercase text-gold/80">
                            Alba C. · 6 meses de tratamiento
                        </span>
                    </div>
                </div>

                {/* CTA */}
                <div className="mt-20 text-center">
                    <button
                        data-testid="punto-cero-cta"
                        onClick={() => scrollTo("contacto")}
                        className="group inline-flex items-center gap-3 px-9 py-4 border border-gold text-cream hover:bg-gold hover:text-navy transition-all duration-300 text-xs sm:text-sm tracking-[0.22em] uppercase font-semibold"
                    >
                        Inicia tu protocolo
                        <ArrowRight
                            size={16}
                            className="transition-transform duration-300 group-hover:translate-x-1.5"
                        />
                    </button>
                    <p className="mt-6 text-[11px] tracking-[0.24em] uppercase text-cream/40">
                        Nutribeauty Clinic & Metabolik Center · COFEPRIS 2515062002A00119
                    </p>
                </div>
            </div>

            <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent" />
        </section>
    );
};

export default ProtocoloPuntoCero;
